import * as React from "react";
import {OnFailure, OnSuccess} from "../../../../../store/action/actionHelpers";
import {connect} from "react-redux";
import {Dispatch} from "redux";
import Toast from "../../../../helper/Toast";
import {none, option, Option} from "ts-option";
import Address from "../../../../../model/address/Address";
import PartnerBrand from "../../../../../model/partnerBrand/PartnerBrand";
import {
	addPartnerBrandRewardsPoints,
	fetchQuoteEmailStyles,
	refreshPartnerBrand,
	updatePartnerBrand,
	updateRewardsData
} from "../../../../../store/action/partnerBrand/partnerBrand";
import {RootState} from "../../../../../store/reducer/root/RootState";
import UpdatePartnerBrandFormView from "./UpdatePartnerBrandFormView";
import Site from "../../../../../model/site/Site";
import PartnerBrandPoints from "../../../../../model/partnerBrand/PartnerBrandPoints";
import {getLoggedUser} from "../../../../../store/view/user";
import PartnerBrandRewardsData from "../../../../../model/partnerBrand/PartnerBrandRewardsData";
import {api} from "../../../../../api/api";
import {getErrorMessage, notifyBugsnagAndShowToast} from "../../../../../utils/Utils";
import Loading from "../../../../ui/loading/Loading";
import PartnerBrandBankData from "../../../../../model/partnerBrand/PartnerBrandBankData";
import QuoteEmailStyle from "../../../../../model/partnerBrand/QuoteEmailStyle";
import {fetchMerchants} from "../../../../../store/action/merchant/merchant";
import DictionaryItem from "../../../../../model/dictionary/DictionaryItem";
import CustomFieldValue from "../../../../../model/job/CustomFieldValue";
import {fetchPartnerBrandCustomFields} from "../../../../../store/action/customField/customField";
import CustomField from "../../../../../model/customField/CustomField";
import {fetchSite} from "../../../../../store/action/site/site";
import {partnerBrandSaveErrorMessage} from "../../sections/partnerBrandSaveErrors";

interface IComponentProps {
	/**
	 * Partner brand.
	 */
	partnerBrand: PartnerBrand;
}

interface IReduxProps {
	/**
	 * Partner brand address.
	 */
	address: Option<Site>;
	/**
	 * Quote email templates styles
	 */
	quoteEmailStyles: Option<QuoteEmailStyle[]>;
	/**
	 * All asset categories.
	 */
	merchants: Option<DictionaryItem[]>;
	/**
	 * Partner brand custom fields list.
	 */
	partnerBrandCustomFields: Option<CustomField[]>;
}

interface IDispatchProps {
	/**
	 * Fetch quote email templates styles
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	fetchQuoteEmailStyles: (onSuccess: OnSuccess, onFailure: OnFailure) => void
	/**
	 * Create partner brand.
	 * @param {partnerBrand} Partner brand
	 * @param {Option<File>} Partner brand logo
	 * @param {Option<Address>} address
	 * @param {OnSuccess} onSuccess
	 * @param {OnFailure} onFailure
	 */
	updatePartnerBrand: (
		partnerBrandId: string,
		partnerBrand: PartnerBrand,
		file: Option<File>,
		address: Option<Address>,
		shouldRemoveFileOnUpdate: boolean,
		onSuccess: OnSuccess,
		onFailure: OnFailure) => void;
	/**
	 * Fetch the partner brand again and put it in the store.
	 */
	refreshPartnerBrand: (partnerBrandId: string) => Promise<PartnerBrand>;
	/**
	 * Fetch merchants
	 * @param {OnFailure} onFailure
	 */
	fetchMerchants: (onFailure: OnFailure) => void
	/**
	 * Fetch partner brand custom fields method
	 */
	fetchPartnerBrandCustomFields: () => void;
	/**
	 * Fetch Site
	 * @param {string} siteId
	 * @param {OnFailure} onFailure
	 */
	fetchSite: (siteId: string, onFailure: OnFailure) => void
}

interface IState {
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
	/**
	 * Brand logo file.
	 */
	file: Option<File>
	/**
	 * Newly added points
	 */
	points: PartnerBrandPoints[];
	/**
	 * Rewards related data.
	 */
	rewardsData: Option<PartnerBrandRewardsData>;
	/**
	 * Bank data.
	 */
	bankData: Option<PartnerBrandBankData>;
	/**
	 * True if fetching rewards related data.
	 */
	isRewardsDataLoading: boolean;
	/**
	 * True if fetching bank related data.
	 */
	isBankDataLoading: boolean;
	/**
	 * Should remove file on update indicator
	 */
	shouldRemoveFileOnUpdate: boolean
	/**
	 * Partner brand custom fields values
	 */
	partnerBrandCustomFieldsValues: Option<CustomFieldValue[]>
	/**
	 * Bumped after each save, so the form is built again from what was saved.
	 */
	savedVersion: number
	/**
	 * Indicates if the brand's address could not be fetched, so the page stops waiting for it.
	 */
	addressFetchFailed: boolean
	/**
	 * Custom fields values as saved, to go back to when changes are discarded.
	 */
	savedCustomFieldsValues: Option<CustomFieldValue[]>
}

type Props = IComponentProps & IDispatchProps & IReduxProps;

class UpdatePartnerBrandForm extends React.PureComponent<Props, IState> {
	/**
	 * Indicates if rewards related data is available for currently logged user.
	 */
	rewardsDataAvailable: boolean;
	/**
	 * Indicates if bank data is available for currently logged user.
	 */
	bankDataAvailable: boolean;

	constructor(props: Props) {
		super(props);
		const loggedUser = getLoggedUser();
		this.rewardsDataAvailable = loggedUser.map(u => u.hasRewardsManagementPerm()).getOrElse(false);
		this.bankDataAvailable = loggedUser.map(u => u.isAdmin()).getOrElse(false);
		this.state = {
			isSubmitting: false,
			file: none,
			points: [],
			rewardsData: none,
			bankData: none,
			isRewardsDataLoading: this.rewardsDataAvailable,
			isBankDataLoading: this.bankDataAvailable,
			shouldRemoveFileOnUpdate: false,
			partnerBrandCustomFieldsValues: none,
			savedVersion: 0,
			addressFetchFailed: false,
			savedCustomFieldsValues: none
		};
		this.onSubmit = this.onSubmit.bind(this);
		this.onAddFile = this.onAddFile.bind(this);
		this.onAddPoints = this.onAddPoints.bind(this);
		this.updateCustomFieldsValues = this.updateCustomFieldsValues.bind(this);
	}

	componentDidMount() {
		if (this.props.quoteEmailStyles.isEmpty) {
			this.props.fetchQuoteEmailStyles(
				() => {},
				(err) => notifyBugsnagAndShowToast(err, "Fetch quote email templates styles"));
		}
		if (this.rewardsDataAvailable) {
			this.fetchRewardsData()
				.then(rewardsData => this.setState({rewardsData: option(rewardsData), isRewardsDataLoading: false}))
				.catch(e => notifyBugsnagAndShowToast(e, "Partner brand rewards data fetch"));
		}
		if (this.bankDataAvailable) {
			this.fetchBankData().then(bankData => this.setState({bankData: option(bankData), isBankDataLoading: false}));
		}
		if (this.props.merchants.isEmpty) {
			this.props.fetchMerchants((e) => notifyBugsnagAndShowToast(e, "Fetch merchants"))
		}

		this.setState({partnerBrandCustomFieldsValues: none}, () =>
			this.fetchCustomFieldsValues()
				.then(values => this.setState({partnerBrandCustomFieldsValues: option(values), savedCustomFieldsValues: option(values)}))
				.catch(err => notifyBugsnagAndShowToast(err, "Fetch partner brand custom fields values")));
		if (!this.props.partnerBrandCustomFields.isDefined) {
			this.props.fetchPartnerBrandCustomFields();
		}
		if (this.props.address.isEmpty) {
			this.props.partnerBrand.siteId.forEach(siteId => this.props.fetchSite(siteId, err => {
				this.setState({addressFetchFailed: true});
				notifyBugsnagAndShowToast(err, "Fetch Site");
			}));
		}
	}

	render() {
		return (
			// The form is built once, as it mounts, so it waits for everything it shows - the address above all:
			// without it a reload showed an empty address, and a save then dropped the brand's site.
			<Loading isLoading={this.state.isRewardsDataLoading || this.state.isBankDataLoading || this.isAddressLoading()}>
				<UpdatePartnerBrandFormView key={this.state.savedVersion}
											onSubmit={this.onSubmit}
											onDiscard={this.discardChanges}
											isSubmitting={this.state.isSubmitting}
											partnerBrand={this.props.partnerBrand}
											address={this.props.address}
											onAddFile={this.onAddFile}
											onRemoveFile={() => this.setState({file: none, shouldRemoveFileOnUpdate: true})}
											points={this.state.points.slice().reverse()}
											onAddPoints={this.onAddPoints}
											rewardsData={this.state.rewardsData}
											bankData={this.state.bankData}
											customFields={this.props.partnerBrandCustomFields}
											customFieldsValues={this.state.partnerBrandCustomFieldsValues}
											updateCustomFieldsValues={this.updateCustomFieldsValues}/>
			</Loading>
		);
	}

	/**
	 * Drops every unsaved change, including those kept here rather than in the form, and builds the form again
	 * from what is saved.
	 */
	discardChanges = () => {
		this.setState({
			file: none,
			shouldRemoveFileOnUpdate: false,
			points: [],
			partnerBrandCustomFieldsValues: this.state.savedCustomFieldsValues,
			savedVersion: this.state.savedVersion + 1
		});
	}

	isAddressLoading(): boolean {
		return this.props.partnerBrand.siteId.isDefined && this.props.address.isEmpty && !this.state.addressFetchFailed;
	}

	onAddFile(file: File) {
		this.setState({file: option(file), shouldRemoveFileOnUpdate: false})
	}

	onSubmit(data: PartnerBrand,
		address: Option<Address>,
		partnerBrandRewardsData: Option<PartnerBrandRewardsData>,
		partnerBrandBankData: Option<PartnerBrandBankData>) {
		let pbDataToUpdate: PartnerBrand = data;
		if (this.bankDataAvailable) {
			partnerBrandBankData.map(bankData => {
				if (bankData.isProvided()) {
					pbDataToUpdate = pbDataToUpdate.copy({
						bankAccountNumber: option(bankData.bankAccountNumber),
						bankSortCode: option(bankData.bankSortCode),
						accountHolder: bankData.accountHolder
					})
				}
			});
		}
		// A new Site only for a new address: every save used to leave the previous Site behind, orphaned.
		const savedAddress = this.props.address.map(site => site.data.address);
		const addressChanged = JSON.stringify(address.orNull) !== JSON.stringify(savedAddress.orNull);
		if (!addressChanged) {
			pbDataToUpdate = pbDataToUpdate.copy({siteId: this.props.partnerBrand.siteId});
		}
		this.setState({isSubmitting: true}, () => {
			this.props.updatePartnerBrand(
				this.props.partnerBrand.id,
				pbDataToUpdate,
				this.state.file,
				addressChanged ? address : none,
				this.state.shouldRemoveFileOnUpdate,
				() => this.afterBrandSaved(partnerBrandRewardsData),
				(error: any) => this.setState({isSubmitting: false}, () =>
					Toast.error(partnerBrandSaveErrorMessage(error, pbDataToUpdate.name), "Partner brand update"))
			);
		});
	}

	/**
	 * Saves what lives outside the brand, once the brand is saved and one after another: the brand's save
	 * replaces the whole brand, so anything saved alongside it could be quietly undone. Then reloads the page
	 * from what was saved, staying on it.
	 */
	afterBrandSaved(rewardsData: Option<PartnerBrandRewardsData>) {
		const partnerBrandId = this.props.partnerBrand.id;
		Promise.resolve()
			.then(() => this.rewardsDataAvailable && this.state.points.length
				? addPartnerBrandRewardsPoints(this.state.points, partnerBrandId) : null)
			.then(() => this.rewardsDataAvailable
				? rewardsData.map(data => updateRewardsData(partnerBrandId, data)).orNull : null)
			.then(() => this.state.partnerBrandCustomFieldsValues.map(values =>
				api.instance.partnerBrandApi.updatePartnerBrandCustomFieldsValues(partnerBrandId,
					values.filter(cfv => cfv.value.isDefined))
					.catch(err => notifyBugsnagAndShowToast(err, "Update partner brand custom fields values"))).orNull)
			.then(() => this.reload())
			.then(() => Toast.success("Brand updated", "Brand update"))
			.catch(err => {
				this.setState({isSubmitting: false});
				notifyBugsnagAndShowToast(err, "Partner brand reload");
			});
	}

	/**
	 * Everything the page shows, fetched again, and the form built again from it.
	 */
	reload(): Promise<void> {
		const partnerBrandId = this.props.partnerBrand.id;
		return Promise.all([
			this.props.refreshPartnerBrand(partnerBrandId),
			this.rewardsDataAvailable ? this.fetchRewardsData().then(option) : Promise.resolve(this.state.rewardsData),
			this.bankDataAvailable ? this.fetchBankData().then(option) : Promise.resolve(this.state.bankData),
			this.fetchCustomFieldsValues().then(option)
		]).then(([, rewardsData, bankData, customFieldsValues]) => {
			this.setState({
				rewardsData,
				bankData,
				partnerBrandCustomFieldsValues: customFieldsValues,
				savedCustomFieldsValues: customFieldsValues,
				points: [],
				file: none,
				shouldRemoveFileOnUpdate: false,
				isSubmitting: false,
				savedVersion: this.state.savedVersion + 1
			});
		});
	}

	fetchRewardsData(): Promise<PartnerBrandRewardsData> {
		return api.instance.partnerBrandApi.fetchRewardsData(this.props.partnerBrand.id);
	}

	/**
	 * Bank data, or empty bank data when there is none yet.
	 */
	fetchBankData(): Promise<PartnerBrandBankData> {
		return api.instance.partnerBrandApi.fetchBankData(this.props.partnerBrand.id)
			.catch(e => {
				if (!(e && e.response && e.response.data.status === 404)) {
					notifyBugsnagAndShowToast(e, "Partner brand bank data fetch", getErrorMessage(e));
				}
				return PartnerBrandBankData.createEmpty();
			});
	}

	fetchCustomFieldsValues(): Promise<CustomFieldValue[]> {
		return api.instance.partnerBrandApi.fetchPartnerBrandCustomFieldsValues(this.props.partnerBrand.id);
	}

	onAddPoints(point: PartnerBrandPoints) {
		this.setState({points: this.state.points.concat(point)})
	}

	updateCustomFieldsValues(customFieldsValues: CustomFieldValue[]) {
		this.setState({partnerBrandCustomFieldsValues: option(customFieldsValues)});
	}
}

function mapStateToProps(state: RootState, props: IComponentProps) {
	const filteredCustomFields = state.customFields.partnerBrandCustomFields.getOrElse([]).filter(item => !item.disabledInUi);
	return {
		address: option(state.site.sitesMap[props.partnerBrand.siteId.orNull]),
		quoteEmailStyles: state.partnerBrand.quoteEmailStyles,
		merchants: state.merchant.merchants,
		partnerBrandCustomFields: state.customFields.partnerBrandCustomFields.isDefined ? option(filteredCustomFields) : none
	}
}

function mapDispatchToProps(dispatch: Dispatch<any>) {
	return {
		fetchQuoteEmailStyles: (onSuccess: OnSuccess, onFailure: OnFailure) =>
			dispatch(fetchQuoteEmailStyles(onSuccess, onFailure)),
		updatePartnerBrand: (partnerBrandId: string,
			partnerBrand: PartnerBrand,
			file: Option<File>,
			address: Option<Address>,
			shouldRemoveFileOnUpdate: boolean,
			onSuccess: OnSuccess,
			onFailure: OnFailure) =>
			dispatch(
				updatePartnerBrand(partnerBrandId, partnerBrand, file, address, shouldRemoveFileOnUpdate, onSuccess, onFailure)),
		refreshPartnerBrand: (partnerBrandId: string) => dispatch(refreshPartnerBrand(partnerBrandId)) as any as Promise<PartnerBrand>,
		fetchMerchants: (onFailure: OnFailure) => dispatch(fetchMerchants(() => {}, onFailure)),
		fetchPartnerBrandCustomFields: () => dispatch(
			fetchPartnerBrandCustomFields(() => {},
				(error) => notifyBugsnagAndShowToast(error, "Fetch partner brand custom fields"))
		),
		fetchSite: (siteId: string, onFailure: OnFailure) => dispatch(fetchSite(siteId, () => {}, onFailure))

	};
}

export default connect<IReduxProps, IDispatchProps, IComponentProps>(mapStateToProps, mapDispatchToProps)(UpdatePartnerBrandForm);
